<template>
  <div class="p-6">
    <!-- 页面标题与操作按钮 -->
    <div class="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">辞书分类管理</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">管理辞书的主分类和子分类</p>
      </div>
      <div class="flex items-center gap-2">
        <button @click="loadCategories"
          class="cancel-btn">
          刷新
        </button>
        <button @click="handleCreateCategory"
          class="primary-btn gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          新增主分类
        </button>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>

    <!-- 分类列表 -->
    <div v-else class="space-y-4">
      <div v-for="category in categories" :key="category.id" class="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 overflow-hidden">
        <!-- 主分类头部 -->
        <div class="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
          <div class="flex items-center gap-4">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ category.displayName }}</h3>
            <span class="text-sm text-gray-500 dark:text-gray-400">({{ category.name }})</span>
            <span class="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full">
              {{ category.bookCount }} 本书籍
            </span>
            <span class="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full">
              {{ category.subcategoryCount }} 个子分类
            </span>
          </div>
          <div class="flex gap-2">
            <button @click="handleCreateSubCategory(category)"
              class="primary-btn text-sm px-3 py-1">
              添加子分类
            </button>
            <button @click="handleEditCategory(category)"
              class="link-btn-edit px-3 py-1 text-sm">
              编辑
            </button>
            <button @click="handleDeleteCategory(category)" class="link-btn-delete px-3 py-1 text-sm">
              删除
            </button>
          </div>
        </div>

        <!-- 子分类列表 -->
        <div v-if="category.subcategories && category.subcategories.length > 0" class="px-6 py-4">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div v-for="subcategory in category.subcategories" :key="subcategory.id"
              class="p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:border-indigo-300 dark:hover:border-indigo-600 transition-colors">
              <div class="flex items-center justify-between">
                <div>
                  <div class="font-medium text-gray-900 dark:text-gray-100">{{ subcategory.displayName }}</div>
                  <div class="text-sm text-gray-500 dark:text-gray-400">({{ subcategory.name }})</div>
                </div>
                <div class="flex gap-1">
                  <button @click="handleEditSubCategory(subcategory)" class="p-1 link-btn-edit"
                    title="编辑">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button @click="handleDeleteSubCategory(subcategory)" class="p-1 link-btn-delete"
                    title="删除">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 无子分类提示 -->
        <div v-else class="px-6 py-4 text-center text-gray-500 dark:text-gray-400 text-sm">
          暂无子分类
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="categories.length === 0" class="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 p-12 text-center">
        <p class="text-gray-500 dark:text-gray-400">暂无分类数据</p>
      </div>
    </div>

    <!-- 主分类表单 Modal -->
    <Modal :show="showCategoryModal" @close="handleCancelCategory">
      <template #header>
        {{ editingCategory ? '编辑主分类' : '新增主分类' }}
      </template>
      <template #body>
        <CategoryForm :category="editingCategory" @submit="handleSubmitCategory" @cancel="handleCancelCategory" />
      </template>
    </Modal>

    <!-- 子分类表单 Modal -->
    <Modal :show="showSubCategoryModal" @close="handleCancelSubCategory">
      <template #header>
        {{ editingSubCategory ? '编辑子分类' : '新增子分类' }}
      </template>
      <template #body>
        <SubCategoryForm :subcategory="editingSubCategory" @submit="handleSubmitSubCategory"
          @cancel="handleCancelSubCategory" />
      </template>
    </Modal>

    <!-- 删除确认对话框 -->
    <ConfirmDialog :show="showDeleteConfirm" type="danger" :title="deleteConfirmTitle" :message="deleteConfirmMessage"
      @confirm="handleConfirmDelete" @cancel="showDeleteConfirm = false" />
  </div>
</template>




<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import {
  getCategoryListAPI,
  createCategoryAPI,
  updateCategoryAPI,
  deleteCategoryAPI,
  createSubCategoryAPI,
  updateSubCategoryAPI,
  deleteSubCategoryAPI,
} from '@/api/dictation/category';
import CategoryForm from '@/components/dictation/CategoryForm.vue';
import SubCategoryForm from '@/components/dictation/SubCategoryForm.vue';
import Modal from '@/components/ui/Modal.vue';
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue';
import { useToast } from '@/composables/useToast';
import type {
  CategoryWithSubCategories,
  SimpleSubCategory,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreateSubCategoryRequest,
  UpdateSubCategoryRequest,
} from '@/types/dictation';

const toast = useToast();

/**
 * 响应式数据
 */
const loading = ref(false);
const categories = ref<CategoryWithSubCategories[]>([]);

// 主分类表单相关
const showCategoryModal = ref(false);
const editingCategory = ref<CategoryWithSubCategories | undefined>(undefined);

// 子分类表单相关
const showSubCategoryModal = ref(false);
const editingSubCategory = ref<SimpleSubCategory | undefined>(undefined);
const currentParentCategory = ref<CategoryWithSubCategories | undefined>(undefined);

// 删除确认对话框相关
const showDeleteConfirm = ref(false);
const deleteConfirmTitle = ref('');
const deleteConfirmMessage = ref('');
const deleteAction = ref<(() => Promise<void>) | null>(null);

/**
 * 加载分类列表
 */
const loadCategories = async () => {
  loading.value = true;
  try {
    const response = await getCategoryListAPI(true, true);
    categories.value = response.data?.categories || [];
  } catch (error) {
    console.error('加载分类列表失败:', error);
    toast.error('加载分类列表失败，请稍后重试');
  } finally {
    loading.value = false;
  }
};

/**
 * 新增主分类
 */
const handleCreateCategory = () => {
  editingCategory.value = undefined;
  showCategoryModal.value = true;
};

/**
 * 编辑主分类
 */
const handleEditCategory = (category: CategoryWithSubCategories) => {
  editingCategory.value = category;
  showCategoryModal.value = true;
};

/**
 * 提交主分类表单
 */
const handleSubmitCategory = async (data: CreateCategoryRequest | UpdateCategoryRequest) => {
  try {
    if (editingCategory.value) {
      // 编辑
      await updateCategoryAPI(editingCategory.value.id, data as UpdateCategoryRequest);
      toast.success('主分类更新成功');
    } else {
      // 新增
      await createCategoryAPI(data as CreateCategoryRequest);
      toast.success('主分类创建成功');
    }
    showCategoryModal.value = false;
    loadCategories();
  } catch (error) {
    console.error('保存主分类失败:', error);
    toast.error('保存失败，请稍后重试');
  }
};

/**
 * 取消主分类表单
 */
const handleCancelCategory = () => {
  showCategoryModal.value = false;
  editingCategory.value = undefined;
};

/**
 * 删除主分类
 */
const handleDeleteCategory = (category: CategoryWithSubCategories) => {
  if (category.bookCount > 0) {
    toast.error('该分类下还有书籍，无法删除');
    return;
  }
  if (category.subcategoryCount > 0) {
    toast.error('该分类下还有子分类，无法删除');
    return;
  }

  deleteConfirmTitle.value = '删除主分类';
  deleteConfirmMessage.value = `确定要删除主分类"${category.displayName}"吗？`;
  deleteAction.value = async () => {
    try {
      await deleteCategoryAPI(category.id);
      toast.success('删除成功');
      loadCategories();
    } catch (error) {
      console.error('删除主分类失败:', error);
      toast.error('删除失败，请稍后重试');
    }
  };
  showDeleteConfirm.value = true;
};

/**
 * 新增子分类
 */
const handleCreateSubCategory = (category: CategoryWithSubCategories) => {
  currentParentCategory.value = category;
  editingSubCategory.value = undefined;
  showSubCategoryModal.value = true;
};

/**
 * 编辑子分类
 */
const handleEditSubCategory = (subcategory: SimpleSubCategory) => {
  editingSubCategory.value = subcategory;
  showSubCategoryModal.value = true;
};

/**
 * 提交子分类表单
 */
const handleSubmitSubCategory = async (data: CreateSubCategoryRequest | UpdateSubCategoryRequest) => {
  try {
    if (editingSubCategory.value) {
      // 编辑
      await updateSubCategoryAPI(editingSubCategory.value.id, data as UpdateSubCategoryRequest);
      toast.success('子分类更新成功');
    } else {
      // 新增
      if (!currentParentCategory.value) {
        toast.error('未选择主分类');
        return;
      }
      await createSubCategoryAPI(currentParentCategory.value.id, data as CreateSubCategoryRequest);
      toast.success('子分类创建成功');
    }
    showSubCategoryModal.value = false;
    loadCategories();
  } catch (error) {
    console.error('保存子分类失败:', error);
    toast.error('保存失败，请稍后重试');
  }
};

/**
 * 取消子分类表单
 */
const handleCancelSubCategory = () => {
  showSubCategoryModal.value = false;
  editingSubCategory.value = undefined;
  currentParentCategory.value = undefined;
};

/**
 * 删除子分类
 */
const handleDeleteSubCategory = (subcategory: SimpleSubCategory) => {
  deleteConfirmTitle.value = '删除子分类';
  deleteConfirmMessage.value = `确定要删除子分类"${subcategory.displayName}"吗？`;
  deleteAction.value = async () => {
    try {
      await deleteSubCategoryAPI(subcategory.id);
      toast.success('删除成功');
      loadCategories();
    } catch (error) {
      console.error('删除子分类失败:', error);
      toast.error('删除失败，请稍后重试');
    }
  };
  showDeleteConfirm.value = true;
};

/**
 * 确认删除
 */
const handleConfirmDelete = async () => {
  if (deleteAction.value) {
    await deleteAction.value();
  }
  showDeleteConfirm.value = false;
  deleteAction.value = null;
};

/**
 * 组件挂载时加载数据
 */
onMounted(() => {
  loadCategories();
});
</script>
